import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContentManagement = () => {
  const [pages, setPages] = useState([]);
  const [contents, setContents] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [deletingContent, setDeletingContent] = useState(null);
  const [contentForm, setContentForm] = useState({ title: "", content: "" });

  useEffect(() => {
    fetchPages();
  }, []);

  useEffect(() => {
    if (selectedPage) {
      fetchContents(selectedPage.id);
    }
  }, [selectedPage]);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/pages`);
      setPages(response.data);
      if (response.data.length > 0) {
        setSelectedPage(response.data[0]);
      }
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const fetchContents = async (pageId) => {
    try {
      const response = await axios.get(`${API}/pages/${pageId}/content`);
      setContents(prev => ({ ...prev, [pageId]: response.data }));
    } catch (error) {
      // May fail if no content exists yet
      setContents(prev => ({ ...prev, [pageId]: [] }));
    }
  };

  const handleOpenDialog = (content = null) => {
    setEditingContent(content);
    setContentForm(content ? { title: content.title, content: content.content } : { title: "", content: "" });
    setDialogOpen(true);
  };

  const handleSaveContent = async (e) => {
    e.preventDefault();
    try {
      if (editingContent) {
        await axios.put(`${API}/pages/content/${editingContent.id}`, contentForm);
        toast.success("Inhalt aktualisiert");
      } else {
        await axios.post(`${API}/pages/${selectedPage.id}/content`, {
          ...contentForm,
          page_id: selectedPage.id
        });
        toast.success("Inhalt erstellt");
      }
      setDialogOpen(false);
      fetchContents(selectedPage.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDeleteContent = async () => {
    try {
      await axios.delete(`${API}/pages/content/${deletingContent.id}`);
      toast.success("Inhalt gelöscht");
      setDeleteDialogOpen(false);
      fetchContents(selectedPage.id);
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const currentContents = contents[selectedPage?.id] || [];

  return (
    <AdminLayout title="Inhalte verwalten">
      <div className="space-y-6" data-testid="content-management">
        <p className="text-muted-foreground">
          Verwalten Sie die Inhalte für jede Buchungskategorie. Teilnehmer sehen nur Inhalte ihrer gebuchten Bereiche.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Page Selector */}
          <Card className="bg-card border-border lg:col-span-1">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-lg">Bereiche</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-1">
                  {pages.map((page) => (
                    <button
                      key={page.id}
                      onClick={() => setSelectedPage(page)}
                      className={`w-full text-left px-3 py-2 rounded-sm text-sm transition-colors ${
                        selectedPage?.id === page.id
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-secondary/50"
                      }`}
                    >
                      {page.name}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Content Editor */}
          <Card className="bg-card border-border lg:col-span-3">
            <CardHeader className="border-b border-border">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Inhalte: {selectedPage?.name || "Wählen Sie einen Bereich"}
                </CardTitle>
                {selectedPage && (
                  <Button onClick={() => handleOpenDialog()} size="sm" className="gap-2">
                    <Plus className="h-4 w-4" />
                    Inhalt hinzufügen
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {!selectedPage ? (
                <div className="py-12 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Wählen Sie einen Bereich aus der Liste</p>
                </div>
              ) : currentContents.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Keine Inhalte für diesen Bereich</p>
                  <Button onClick={() => handleOpenDialog()} variant="outline" className="mt-4">
                    Ersten Inhalt erstellen
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {currentContents.map((content) => (
                    <div 
                      key={content.id}
                      className="p-4 bg-secondary/30 rounded-sm border border-border"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h4 className="font-medium text-primary mb-2">{content.title}</h4>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-3">
                            {content.content || "Kein Inhalt"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(content)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setDeletingContent(content); setDeleteDialogOpen(true); }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingContent ? "Inhalt bearbeiten" : "Neuer Inhalt"}</DialogTitle>
              <DialogDescription>
                Dieser Inhalt wird für Teilnehmer mit "{selectedPage?.name}" Buchung sichtbar.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSaveContent} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titel</Label>
                <Input
                  id="title"
                  value={contentForm.title}
                  onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                  placeholder="Titel des Inhalts"
                  required
                  className="bg-background"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Inhalt</Label>
                <Textarea
                  id="content"
                  value={contentForm.content}
                  onChange={(e) => setContentForm({ ...contentForm, content: e.target.value })}
                  placeholder="Inhalt (Text, Anleitungen, Links...)"
                  rows={10}
                  className="bg-background resize-none"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit">
                  {editingContent ? "Speichern" : "Erstellen"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Inhalt löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie "{deletingContent?.title}" wirklich löschen?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteContent} className="bg-destructive text-destructive-foreground">
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default ContentManagement;
